import { Box, Button, Flex, IconButton, Stack, useDisclosure } from '@chakra-ui/react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { PlusSquareIcon } from '@chakra-ui/icons';
import { FiRefreshCcw } from 'react-icons/fi';
import { AbsenceRecord, AbsenceStatus, AbsenceType, User } from '../../types';
import { useGetAbsencesPagination } from '../../query/resource-hooks/absence';
import { AbsenceListFilter } from './AbsenceListFilter';
import { AbsenceTable } from './AbsenceTable';
import { CreateAbsenceDrawer } from './CreateAbsenceDrawer';
import { AbsenceDetailDrawer } from './AbsenceDetailDrawer';
import { DateTime } from 'luxon';

type AbsenceListTabProps = {
    defaultUser?: User;
};

export const AbsenceListTab: React.FC<AbsenceListTabProps> = ({ defaultUser }) => {
    const intl = useIntl();
    const [selectedDates, setSelectedDates] = useState<Date[]>([]);
    const [selectedType, setSelectedType] = useState<AbsenceType | undefined>();
    const [selectedStatus, setSelectedStatus] = useState<AbsenceStatus | undefined>();
    const [selectedAbsenceId, setSelectedAbsenceId] = useState<number | null>(null);
    const createDrawer = useDisclosure();
    const detailDrawer = useDisclosure();

    const absenceQuery = useGetAbsencesPagination({
        ...(selectedDates[0] && { dateFrom: DateTime.fromJSDate(selectedDates[0]).toFormat('yyyy-MM-dd') }),
        ...(selectedDates[1] && { dateTo: DateTime.fromJSDate(selectedDates[1]).toFormat('yyyy-MM-dd') }),
        ...(selectedType && { type: selectedType }),
        ...(selectedStatus && { status: selectedStatus }),
        ...(defaultUser && { userIds: [defaultUser.id] }),
    });

    function handleItemClick(item: AbsenceRecord) {
        setSelectedAbsenceId(item.id);
        detailDrawer.onOpen();
    }

    return (
        <Box display="flex" justifyContent="center">
            <Box maxW={1680} width={'100%'}>
                <Stack flexDir={{ base: 'column', lg: 'row' }} width={'100%'}>
                    <Box order={{ base: 2, lg: 1 }} width={'100%'}>
                        <Box>
                            <Flex justify={'space-between'} align={'center'}>
                                <Button ml={2} onClick={createDrawer.onOpen} leftIcon={<PlusSquareIcon />} boxShadow={'md'}>
                                    {intl.formatMessage({ id: 'absence.create' })}
                                </Button>

                                <IconButton
                                    icon={<FiRefreshCcw />}
                                    aria-label="refresh"
                                    m={2}
                                    variant={'text'}
                                    onClick={() => absenceQuery.refetch()}
                                />
                            </Flex>

                            <AbsenceTable
                                data={absenceQuery.data}
                                fetchNextPage={absenceQuery.fetchNextPage}
                                hasNextPage={absenceQuery.hasNextPage}
                                isFetchingNextPage={absenceQuery.isFetchingNextPage}
                                isLoading={absenceQuery.isLoading || absenceQuery.isRefetching}
                                onItemClick={handleItemClick}
                                onReviewSuccess={() => absenceQuery.refetch()}
                            />
                        </Box>
                    </Box>
                    <Box order={{ base: 1, lg: 2 }}>
                        <AbsenceListFilter
                            onDateChange={setSelectedDates}
                            selectedDates={selectedDates}
                            selectedType={selectedType}
                            onTypeChange={setSelectedType}
                            selectedStatus={selectedStatus}
                            onStatusChange={setSelectedStatus}
                            totalRecords={absenceQuery.data?.pages?.[0]?.meta?.total}
                            position="sticky"
                            top="22px"
                            mt={4}
                            ml={{ base: 2, lg: 0 }}
                            mr={2}
                        />
                    </Box>
                </Stack>

                <CreateAbsenceDrawer
                    initialUser={defaultUser}
                    isOpen={createDrawer.isOpen}
                    onClose={createDrawer.onClose}
                    onSuccess={() => absenceQuery.refetch()}
                />

                <AbsenceDetailDrawer
                    isOpen={detailDrawer.isOpen}
                    onClose={detailDrawer.onClose}
                    absenceId={selectedAbsenceId}
                    onActionComplete={() => absenceQuery.refetch()}
                />
            </Box>
        </Box>
    );
};
